import { AggregateMethod, ControlType, Property, PropertyType } from "../../../../base/types";
import { FC, useEffect, useState } from "react";

import { ApexOptions } from "apexcharts";
import Chart from "react-apexcharts"
import { IControlAttributes } from "../../../../base/models";
import { Registry } from "../../../../base/register";
import _ from "lodash";
import clsx from "clsx";
import { useDataContext } from "../../../page/context";
import { useGetProperties } from "../../../../base/hooks";

export const PieChartComponent: FC<IControlAttributes> = ({
    control, className, children, props, designerMode, ...rest
}) => {
    const { properties, getPropertyValue } = useGetProperties({ bindings: control.bindings })
    const tooltip: string | undefined = properties[Property.Tooltip]
    const donut: boolean | undefined = properties[Property.Donut]
    const legends: boolean | undefined = properties[Property.ShowLabels]
    const toolbar: boolean | undefined = properties[Property.ShowToolbar]
    const monochrome: boolean | undefined = properties[Property.Monochrome]
    const gradient: boolean | undefined = properties[Property.Gradient]
    const unknownLabel: string | undefined = properties[Property.UnknownLabel]
    const height: number | undefined = properties[Property.Height]
    const aggregateMethod: AggregateMethod | undefined = properties[Property.AggregateMethod]

    const legendsField: string | undefined = getPropertyValue(Property.LabelsField)
    const seriesField: string | undefined = getPropertyValue(Property.ValuesField)
    const colorMapJsonText: string | undefined = getPropertyValue(Property.ColorMap)

    const [chartType, setChartType] = useState<"pie" | "donut">("pie")
    const { type, result: data } = useDataContext()

    const [options, setOptions] = useState<ApexOptions>({
        theme: { monochrome: { enabled: monochrome } },
        chart: {
            width: '100%',
            toolbar: { show: toolbar },
        },
        fill: { type: gradient ? 'gradient' : '' },
        labels: [],
        legend: { show: legends },
    });

    useEffect(() => {
        setOptions({
            theme: { monochrome: { enabled: monochrome } },
            chart: {
                width: '100%',
                toolbar: { show: toolbar },
            },
            fill: { type: gradient ? 'gradient' : '' },
            labels: options.labels,
            legend: { show: legends },
        })
        // eslint-disable-next-line
    }, [monochrome, gradient, legends, chartType])

    const [series, setSeries] = useState<ApexNonAxisChartSeries>([]);

    useEffect(() => {
        setChartType(donut ? "donut" : "pie")
        // eslint-disable-next-line
    }, [donut])

    const aggregateChartData = () => {
        if (type !== "List" || !aggregateMethod || !legendsField || !seriesField) {
            return;
        }

        const unknownText = unknownLabel || 'Unknown';

        const rows = data.records.map(row => ({
            name: row[legendsField] || unknownText as string,
            value: Number(row[seriesField] || 0),
        }))

        let chartData: { name: string, aggregate: number, count: number }[] = []

        switch (aggregateMethod) {
            case AggregateMethod.Count:
                rows.reduce((agg, row) => {
                    const item = chartData.find(p => p.name === row.name)
                    if (!item) {
                        agg.push({ name: row.name, aggregate: 1, count: 1 })
                    } else {
                        item.aggregate += 1
                        item.count += 1
                    }

                    return agg;
                }, chartData)
                break;
            case AggregateMethod.Sum:
                rows.reduce((agg, row) => {
                    const item = chartData.find(p => p.name === row.name)
                    if (!item) {
                        agg.push({ name: row.name, aggregate: row.value, count: 1 })
                    } else {
                        item.aggregate += row.value
                        item.count += 1
                    }

                    return agg;
                }, chartData)
                break;
            case AggregateMethod.Minimum:
                rows.reduce((agg, row) => {
                    const item = chartData.find(p => p.name === row.name)
                    if (!item) {
                        agg.push({ name: row.name, aggregate: row.value, count: 1 })
                    } else {
                        item.aggregate = item.aggregate > row.value ? row.value : item.aggregate
                    }

                    return agg;
                }, chartData)
                break;
            case AggregateMethod.Maximum:
                rows.reduce((agg, row) => {
                    const item = chartData.find(p => p.name === row.name)
                    if (!item) {
                        agg.push({ name: row.name, aggregate: row.value, count: 1 })
                    } else {
                        item.aggregate = item.aggregate < row.value ? row.value : item.aggregate
                    }

                    return agg;
                }, chartData)
                break;
            case AggregateMethod.Average:
                rows.reduce((agg, row) => {
                    const item = chartData.find(p => p.name === row.name)
                    if (!item) {
                        agg.push({ name: row.name, aggregate: row.value, count: 1 })
                    } else {
                        item.aggregate += row.value
                        item.count += 1
                    }

                    return agg;
                }, chartData)
                chartData.forEach(p => p.aggregate = p.aggregate / p.count);
                break;
        }

        chartData = _.sortBy(chartData, p => p.name)
        return chartData;
    }

    const getColors = (labels: string[], colorMapJsonText?: string) => {
        try {
            if (!colorMapJsonText) return;
            const colorMap = JSON.parse(colorMapJsonText);
            return labels.map(p => (colorMap[p] || null));
        } catch (err) { console.log(err) }
    }

    useEffect(() => {
        const chartData = aggregateChartData();
        if (!chartData) return;

        const labels = chartData.map(p => p.name);
        const series = chartData.map(p => p.aggregate);
        const colors = getColors(labels, colorMapJsonText)

        setOptions({ ...options, labels, colors })
        setSeries(series)

        // eslint-disable-next-line
    }, [type, data, properties])

    return (
        <div title={tooltip} className={clsx(className)}>
            <Chart options={options} series={series} type={chartType} height={height || 150} />
        </div>)
}

Registry.addControl({
    id: "db6a198b-4de1-40a7-b297-d39b9625a1f7",
    name: "Pie Chart",
    type: ControlType.PieChart,
    properties: [
        { id: Property.Donut, name: 'Donut', type: PropertyType.Boolean },
        { id: Property.ShowToolbar, name: 'Show Toolbar', type: PropertyType.Boolean },
        { id: Property.ShowLabels, name: 'Show Labels', type: PropertyType.Boolean },
        { id: Property.LabelsField, name: 'Labels Field', type: PropertyType.String },
        { id: Property.ValuesField, name: 'Values Field', type: PropertyType.String },
        { id: Property.UnknownLabel, name: 'Unknown Label', type: PropertyType.String },
        { id: Property.AggregateMethod, name: 'Method', type: PropertyType.AggregateMethod },
        { id: Property.Monochrome, name: 'Monochrome', type: PropertyType.Boolean },
        { id: Property.Gradient, name: 'Gradient', type: PropertyType.Boolean },
        { id: Property.Tooltip, name: 'Tooltip', type: PropertyType.String },
        { id: Property.Height, name: 'Height', type: PropertyType.Number },
        { id: Property.ColorMap, name: 'Color Map', type: PropertyType.String },
    ],
    component: PieChartComponent
})
